import React, { useEffect, useState } from "react";
import axios from "axios";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import DefaultView from "../components/DefaultView";
import { connect } from "react-redux";
import { toast as message } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { actionCreateStripeCustomer } from "../store/actions/subscriptionAction";
import { useSelector } from "react-redux";

const numberSkeletonStyle = `
  @keyframes fpn-shimmer {
    0%   { background-position: -500px 0; }
    100% { background-position: 500px 0; }
  }
  .fpn-skel {
    background: linear-gradient(90deg, #ededf0 25%, #e0e0e6 50%, #ededf0 75%);
    background-size: 500px 100%;
    animation: fpn-shimmer 1.3s infinite linear;
    border-radius: 6px;
    display: block;
  }
`;

const NSkel = ({ w, h = 13, rounded = false, style = {} }) => (
  <span
    className="fpn-skel"
    style={{ width: w, height: h, borderRadius: rounded ? 999 : 6, display: "block", ...style }}
  />
);

const NumberCardSkeleton = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "9px 12px",
      borderRadius: 8,
      border: "1.5px solid #ededf0",
      background: "#fafafa",
      marginBottom: 5,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <NSkel w={36} h={36} rounded />
      <div>
        <NSkel w={128} h={14} />
        <NSkel w={72} h={11} style={{ marginTop: 6 }} />
      </div>
    </div>
    <NSkel w={22} h={22} rounded />
  </div>
);

const CARD_STYLE = {
  style: {
    base: {
      fontSize: "15px",
      color: "#495057",
      fontFamily: "inherit",
      "::placeholder": { color: "#adb5bd" },
    },
    invalid: { color: "#f46a6a" },
  },
};

const StepIndicator = ({ active }) => {
  const steps = [
    { num: 1, label: "Select Country", icon: "bx-globe" },
    { num: 2, label: "Find Numbers", icon: "bx-search-alt" },
    { num: 3, label: "Checkout", icon: "bx-credit-card" },
  ];
  return (
    <div
      style={{
        display: "flex",
        alignItems: "stretch",
        background: "#f4f5ff",
        borderRadius: 14,
        padding: "6px",
        marginBottom: 20,
        gap: 4,
      }}
    >
      {steps.map((step) => {
        const isDone = active > step.num;
        const isCurrent = active === step.num;
        return (
          <div
            key={step.num}
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              padding: "10px 12px",
              borderRadius: 10,
              background: isCurrent ? "#556ee6" : isDone ? "#eef0fb" : "transparent",
              boxShadow: isCurrent ? "0 2px 12px rgba(85,110,230,0.28)" : "none",
              transition: "all 0.25s",
            }}
          >
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: isCurrent ? "rgba(255,255,255,0.22)" : isDone ? "#556ee6" : "#e0e3f0",
                flexShrink: 0,
              }}
            >
              {isDone ? (
                <i className="bx bx-check" style={{ fontSize: 16, color: "#fff" }} />
              ) : (
                <i
                  className={`bx ${step.icon}`}
                  style={{
                    fontSize: 15,
                    color: isCurrent ? "#fff" : "#9199a9",
                  }}
                />
              )}
            </div>
            <div style={{ lineHeight: 1.2 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: isCurrent ? "rgba(255,255,255,0.75)" : isDone ? "#556ee6" : "#adb5bd",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Step {step.num}
              </div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: isCurrent ? "#fff" : isDone ? "#556ee6" : "#9199a9",
                  whiteSpace: "nowrap",
                }}
              >
                {step.label}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const NumberCard = ({ num, selected, onToggle }) => (
  <div
    onClick={() => onToggle(num)}
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "9px 12px",
      borderRadius: 8,
      border: selected ? "2px solid #556ee6" : "1.5px solid #e9ecef",
      background: selected ? "#f4f5ff" : "#fff",
      cursor: "pointer",
      transition: "all 0.18s",
      marginBottom: 5,
    }}
  >
    <div className="d-flex align-items-center gap-2">
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          background: selected ? "#556ee6" : "#f0f2f7",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          transition: "background 0.18s",
        }}
      >
        <i
          className="bx bx-phone"
          style={{ fontSize: 18, color: selected ? "#fff" : "#9199a9" }}
        />
      </div>
      <div>
        <div style={{ fontWeight: 600, fontSize: 15, color: "#343a40", letterSpacing: 0.3 }}>
          {num.phone_number}
        </div>
        {num.locality && (
          <div style={{ fontSize: 12, color: "#9199a9" }}>
            {num.locality}{num.region ? `, ${num.region}` : ""}
          </div>
        )}
      </div>
    </div>
    <div
      style={{
        width: 22,
        height: 22,
        borderRadius: "50%",
        border: selected ? "2px solid #556ee6" : "2px solid #ced4da",
        background: selected ? "#556ee6" : "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        transition: "all 0.18s",
      }}
    >
      {selected && <i className="bx bx-check" style={{ fontSize: 14, color: "#fff" }} />}
    </div>
  </div>
);

const FaxPhoneNumbers = ({ user, actionCreateStripeCustomer }) => {
  // stripeCustomer from Redux is set after create-customer is called in this session
  const stripeCustomer = useSelector((state) => state.subscription?.stripeCustomer);
  const TWILLIO_BASE_URL = process.env.REACT_APP_TWILLIO_BASE_URL;
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();

  const [activeTab, setActiveTab] = useState(1);
  const [selectedCountry, setSelectedCountry] = useState("US");
  const [areaCode, setAreaCode] = useState("");
  const [numberResult, setNumberResult] = useState(null);
  const [searchNumberLoader, setSearchNumberLoader] = useState(false);
  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const [systemConfig, setSystemConfig] = useState(null);
  const [isAutoSuggestNumber, setIsAutoSuggestNumber] = useState(false);

  const [planPricing, setPlanPricing] = useState({
    monthly: 6.99,
    planCode: null,
    freeNumberIncluded: false,
    freeSlotUsed: false,
  });

  const [cardholderName, setCardholderName] = useState("");
  const [cardError, setCardError] = useState(null);
  const [checkoutLoader, setCheckoutLoader] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);

  useEffect(() => {
    getSystemConfig();
    loadPlanPricing();
  }, []);

  const getSystemConfig = async () => {
    try {
      const res = await axios.get(`${TWILLIO_BASE_URL}/numbers/get_system_config`);
      setSystemConfig(res.data);
    } catch {
      // use defaults if unavailable
    }
  };

  const loadPlanPricing = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/subscription/fax-number-pricing`, {
        headers: { Authorization: localStorage.getItem("authToken") },
      });
      // response shape: { result: { data: { status, data: { monthly, planCode, ... } } } }
      const pricing = res.data?.result?.data?.data;
      if (pricing?.monthly != null) {
        setPlanPricing(pricing);
      }
    } catch {
      // keep default $6.99
    }
  };

  const setupFee = parseFloat(systemConfig?.find?.((s) => s.config_item === "SETUP_FEES")?.value ?? 1.0);

  const freeCountInSelection =
    planPricing.freeNumberIncluded && !planPricing.freeSlotUsed ? 1 : 0;

  const priceForNumber = (index) =>
    index < freeCountInSelection ? 0 : parseFloat(planPricing.monthly);

  const monthlyTotal = selectedNumbers.reduce((sum, _, i) => sum + priceForNumber(i), 0);

  const handleSearchNumber = async () => {
    setSearchNumberLoader(true);
    setNumberResult(null);
    try {
      const res = await axios.post(`${TWILLIO_BASE_URL}/numbers/search_number`, {
        country_code: selectedCountry,
        area_code: areaCode,
      });
      if (res.data?.status === "ERR") {
        message.error(res.data.ErrorMessage || "No numbers found.");
        setNumberResult([]);
      } else {
        setNumberResult(Array.isArray(res.data) ? res.data : []);
      }
    } catch {
      message.error("Failed to fetch numbers. Please try again.");
      setNumberResult([]);
    } finally {
      setSearchNumberLoader(false);
    }
  };

  useEffect(() => {
    if (isAutoSuggestNumber) {
      setNumberResult(null);
      setSelectedNumbers([]);
      setAreaCode("");
      handleSearchNumber();
    } else {
      setNumberResult(null);
      setSelectedNumbers([]);
    }
  }, [isAutoSuggestNumber]);

  const toggleNumber = (num) => {
    setSelectedNumbers((prev) => {
      const exists = prev.find((n) => n.phone_number === num.phone_number);
      if (exists) return prev.filter((n) => n.phone_number !== num.phone_number);
      return [...prev, num];
    });
  };

  const isSelected = (num) =>
    selectedNumbers.some((n) => n.phone_number === num.phone_number);

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (!isAgreed) { message.error("Please agree to the terms and conditions."); return; }
    if (selectedNumbers.length === 0) { message.error("No numbers selected."); return; }
    if (!cardholderName.trim()) { message.error("Please enter the cardholder name."); return; }

    setCheckoutLoader(true);
    setCardError(null);

    try {
      const cardElement = elements.getElement(CardElement);

      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: { name: cardholderName },
      });
      if (pmError) { setCardError(pmError.message); setCheckoutLoader(false); return; }

      // Use stored stripe_customer_id from user record first, then Redux session fallback
      let customerId = user?.stripe_customer_id || stripeCustomer?.id;
      if (!customerId) {
        const customer = await actionCreateStripeCustomer({
          email: user?.user_email,
          name: user?.user_fullname || cardholderName,
        });
        customerId = customer?.id;
        if (!customerId) throw new Error("Failed to create Stripe customer.");
      }

      const buyRes = await axios.post(
        `${TWILLIO_BASE_URL}/numbers/buy_number`,
        {
          number: selectedNumbers.map((n) => n.phone_number),
          number_country_code: 1,
          number_city_code: "251",
          number_type: "FAX",
          number_status: "enabled",
          provider_Name: "TWILIO",
          user_ID: user.user_ID,
          currency: "USD",
        },
        { headers: { Authorization: localStorage.getItem("authToken") } }
      );

      const purchased = buyRes.data?.successfulPurchases ?? [];
      if (purchased.length === 0) {
        throw new Error(buyRes.data?.errors?.[0]?.Error ?? "Failed to purchase numbers.");
      }

      const faxNumberIds = purchased.map((p) => p.data?.number_ID).filter(Boolean);

      const subRes = await axios.post(
        `${BASE_URL}/subscription/create-fax-subscriptions`,
        { customerId, paymentMethodId: paymentMethod.id, faxNumberIds },
        { headers: { Authorization: localStorage.getItem("authToken") } }
      );

      const subResults = subRes.data?.result?.data?.results ?? [];
      if (subResults.length === 0) throw new Error("Failed to create subscriptions.");

      for (const sub of subResults) {
        if (sub.clientSecret) {
          const { error: confirmError } = await stripe.confirmCardPayment(sub.clientSecret);
          if (confirmError) throw new Error(confirmError.message);
        }
      }

      message.success(`${purchased.length} number(s) subscribed successfully!`);
      navigate("/fax-number-subscriptions");
    } catch (err) {
      message.error(err.message ?? "Something went wrong. Please try again.");
    } finally {
      setCheckoutLoader(false);
    }
  };

  return (
    <div className="main-content">
      <style>{numberSkeletonStyle}</style>
      <div className="page-content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 className="mb-sm-0 main_heading">Buy Fax Numbers</h4>
                <div className="page-title-right">
                  <DefaultView />
                </div>
              </div>
            </div>
          </div>

          <div className="row justify-content-center">
            <div className="col-xl-7 col-lg-9 col-md-10">
              <div
                className="card mt-3"
                style={{ borderRadius: 16, boxShadow: "0 4px 24px rgba(0,0,0,0.07)", border: "none" }}
              >
                <div className="card-body p-4">
                  <StepIndicator active={activeTab} />

                  {/* ── Step 1: Select Country ── */}
                  {activeTab === 1 && (
                    <div>
                      <div className="mb-3">
                        <label className="form-label fw-semibold mb-1">Country</label>
                        <select
                          className="form-select form-select-lg"
                          style={{ borderRadius: 10, border: "1.5px solid #e9ecef" }}
                          value={selectedCountry}
                          onChange={(e) => setSelectedCountry(e.target.value)}
                        >
                          <option value="US">🇺🇸 United States</option>
                        </select>
                      </div>

                      <div
                        className="d-flex align-items-center justify-content-between p-3 mb-3"
                        style={{ background: "#f8f9ff", borderRadius: 10, border: "1.5px solid #e0e4ff" }}
                      >
                        <label htmlFor="autoSuggest" style={{ cursor: "pointer", marginBottom: 0 }}>
                          <div className="fw-semibold" style={{ fontSize: 14, color: "#343a40" }}>
                            Auto-suggest available numbers
                          </div>
                          <div style={{ fontSize: 12, color: "#9199a9", marginTop: 2 }}>
                            Skip area code search and see numbers immediately
                          </div>
                        </label>
                        <div className="form-check form-switch mb-0 ms-3">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="autoSuggest"
                            checked={isAutoSuggestNumber}
                            onChange={(e) => setIsAutoSuggestNumber(e.target.checked)}
                            style={{ width: 42, height: 22, cursor: "pointer" }}
                          />
                        </div>
                      </div>

                      <div className="d-flex justify-content-end">
                        <button
                          className="btn btn-primary px-5"
                          style={{ borderRadius: 10, fontWeight: 600 }}
                          onClick={() => setActiveTab(2)}
                        >
                          Continue <i className="bx bx-chevron-right ms-1" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ── Step 2: Find Numbers ── */}
                  {activeTab === 2 && (
                    <div>
                      {/* Area code search */}
                      <div className="mb-3">
                        <label className="form-label fw-semibold">Area Code</label>
                        <div className="input-group" style={{ borderRadius: 10, overflow: "hidden" }}>
                          <span
                            className="input-group-text"
                            style={{ background: "#f8f9ff", border: "1.5px solid #e9ecef", borderRight: 0 }}
                          >
                            <i className="bx bx-hash text-primary" />
                          </span>
                          <input
                            type="number"
                            className="form-control"
                            style={{ border: "1.5px solid #e9ecef", borderLeft: 0, borderRight: 0 }}
                            value={areaCode}
                            onChange={(e) => setAreaCode(e.target.value)}
                            placeholder="e.g. 415"
                            disabled={isAutoSuggestNumber}
                            onKeyDown={(e) => e.key === "Enter" && handleSearchNumber()}
                          />
                          <button
                            className="btn btn-primary px-4"
                            type="button"
                            onClick={handleSearchNumber}
                            disabled={searchNumberLoader}
                            style={{ borderRadius: "0 10px 10px 0", fontWeight: 600 }}
                          >
                            {searchNumberLoader ? (
                              <span className="spinner-border spinner-border-sm" />
                            ) : (
                              <><i className="bx bx-search me-1" />Search</>
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Plan pricing badge */}
                      {planPricing.planCode && (
                        <div
                          className="d-flex align-items-center gap-2 mb-3 px-3 py-2"
                          style={{
                            background: "#f4f5ff",
                            borderRadius: 10,
                            border: "1.5px solid #dde0ff",
                            fontSize: 13,
                          }}
                        >
                          <i className="bx bx-crown text-primary" />
                          <span>
                            <strong className="text-capitalize">{planPricing.planCode}</strong> plan —{" "}
                            <strong>${parseFloat(planPricing.monthly).toFixed(2)}/mo</strong> per number
                          </span>
                          {planPricing.freeNumberIncluded && !planPricing.freeSlotUsed && (
                            <span className="badge bg-success ms-auto">1 Free number</span>
                          )}
                          {planPricing.freeNumberIncluded && planPricing.freeSlotUsed && (
                            <span className="badge bg-secondary ms-auto">Free slot used</span>
                          )}
                        </div>
                      )}

                      {/* Loading skeleton */}
                      {searchNumberLoader && (
                        <div>
                          <div
                            className="d-flex align-items-center justify-content-between mb-2"
                          >
                            <NSkel w={100} h={13} />
                            <NSkel w={72} h={22} rounded />
                          </div>
                          <div
                            style={{
                              borderRadius: 10,
                              border: "1.5px solid #ededf0",
                              padding: "6px",
                            }}
                          >
                            {[1, 2, 3, 4, 5].map((i) => (
                              <NumberCardSkeleton key={i} />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Empty state */}
                      {!searchNumberLoader && numberResult && numberResult.length === 0 && (
                        <div
                          className="text-center py-4"
                          style={{
                            background: "#fff8f0",
                            borderRadius: 12,
                            border: "1.5px solid #ffe4c4",
                          }}
                        >
                          <i className="bx bx-phone-off" style={{ fontSize: 36, color: "#f5a623" }} />
                          <p className="fw-semibold mb-1 mt-2">No numbers found</p>
                          <p className="text-muted mb-2" style={{ fontSize: 13 }}>
                            Try a different area code or clear it to search all.
                          </p>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => { setAreaCode(""); handleSearchNumber(); }}
                          >
                            Search all US numbers
                          </button>
                        </div>
                      )}

                      {/* Number list */}
                      {!searchNumberLoader && numberResult && numberResult.length > 0 && (
                        <>
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <label className="form-label fw-semibold mb-0" style={{ fontSize: 13 }}>
                              Select Numbers
                            </label>
                            <span
                              className="badge"
                              style={{ background: "#eef0fb", color: "#556ee6", fontWeight: 600, fontSize: 12 }}
                            >
                              {numberResult.length} available
                            </span>
                          </div>
                          <div
                            style={{
                              maxHeight: 240,
                              overflowY: "auto",
                              borderRadius: 10,
                              border: "1.5px solid #e9ecef",
                              padding: "6px",
                            }}
                          >
                            {numberResult.map((num) => (
                              <NumberCard
                                key={num.phone_number}
                                num={num}
                                selected={isSelected(num)}
                                onToggle={toggleNumber}
                              />
                            ))}
                          </div>
                        </>
                      )}

                      {/* Selection summary */}
                      {selectedNumbers.length > 0 && (
                        <div
                          className="d-flex align-items-center justify-content-between mt-3 px-3 py-2"
                          style={{
                            background: "#f4f5ff",
                            borderRadius: 10,
                            border: "1.5px solid #dde0ff",
                          }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <span
                              className="badge"
                              style={{ background: "#556ee6", fontSize: 13, fontWeight: 700 }}
                            >
                              {selectedNumbers.length}
                            </span>
                            <span style={{ fontSize: 14, color: "#495057" }}>
                              number{selectedNumbers.length > 1 ? "s" : ""} selected
                            </span>
                          </div>
                          <span style={{ fontWeight: 700, color: "#556ee6", fontSize: 15 }}>
                            ${monthlyTotal.toFixed(2)}/mo
                          </span>
                        </div>
                      )}

                      <div className="d-flex justify-content-between mt-3">
                        <button
                          className="btn btn-light px-4"
                          style={{ borderRadius: 10, fontWeight: 600 }}
                          onClick={() => setActiveTab(1)}
                        >
                          <i className="bx bx-chevron-left me-1" />Back
                        </button>
                        <button
                          className="btn btn-primary px-5"
                          style={{ borderRadius: 10, fontWeight: 600 }}
                          onClick={() => setActiveTab(3)}
                          disabled={selectedNumbers.length === 0}
                        >
                          Continue <i className="bx bx-chevron-right ms-1" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ── Step 3: Checkout ── */}
                  {activeTab === 3 && (
                    <div>
                      {/* Order summary */}
                      <div
                        className="mb-3"
                        style={{
                          borderRadius: 12,
                          border: "1.5px solid #e0e4ff",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          className="d-flex align-items-center justify-content-between px-3 py-2"
                          style={{ background: "#f4f5ff", borderBottom: "1.5px solid #e0e4ff" }}
                        >
                          <span style={{ fontWeight: 700, fontSize: 14, color: "#343a40" }}>
                            Order Summary
                          </span>
                          {planPricing.planCode && (
                            <span
                              className="badge"
                              style={{
                                background: planPricing.planCode === "business" ? "#34c38f" : "#556ee6",
                                fontSize: 11,
                                fontWeight: 600,
                              }}
                            >
                              {planPricing.planCode === "business" ? "Business" : "Starter"} Plan
                            </span>
                          )}
                        </div>

                        <div className="p-3">
                          {selectedNumbers.map((num, i) => {
                            const isFree = i < freeCountInSelection;
                            return (
                              <div
                                key={i}
                                className="d-flex align-items-center justify-content-between mb-2"
                                style={{
                                  padding: "10px 12px",
                                  borderRadius: 8,
                                  background: isFree ? "#f0fff8" : "#f8f9fa",
                                  border: isFree ? "1px solid #b7edd3" : "1px solid #e9ecef",
                                }}
                              >
                                <div className="d-flex align-items-center gap-2">
                                  <i className="bx bx-phone text-primary" />
                                  <span style={{ fontWeight: 600, fontSize: 14 }}>
                                    {num.phone_number}
                                  </span>
                                  {isFree && (
                                    <span
                                      className="badge bg-success"
                                      style={{ fontSize: 10, fontWeight: 700 }}
                                    >
                                      FREE
                                    </span>
                                  )}
                                </div>
                                <span
                                  style={{
                                    fontWeight: 700,
                                    fontSize: 14,
                                    color: isFree ? "#34c38f" : "#495057",
                                  }}
                                >
                                  {isFree ? "$0.00" : `$${parseFloat(planPricing.monthly).toFixed(2)}`}
                                  <span style={{ fontSize: 11, fontWeight: 400, color: "#9199a9" }}>/mo</span>
                                </span>
                              </div>
                            );
                          })}

                          <div
                            style={{
                              borderTop: "1.5px dashed #e0e4ff",
                              marginTop: 8,
                              paddingTop: 10,
                            }}
                          >
                            <div className="d-flex justify-content-between mb-1">
                              <span style={{ fontSize: 13, color: "#6c757d" }}>Monthly recurring</span>
                              <span style={{ fontWeight: 700, color: "#556ee6" }}>
                                ${monthlyTotal.toFixed(2)}/mo
                              </span>
                            </div>
                            {setupFee > 0 && (
                              <div className="d-flex justify-content-between mb-1">
                                <span style={{ fontSize: 13, color: "#6c757d" }}>Setup fee (×{selectedNumbers.length})</span>
                                <span style={{ fontWeight: 600, fontSize: 13 }}>
                                  ${(setupFee * selectedNumbers.length).toFixed(2)}
                                </span>
                              </div>
                            )}
                            <div
                              className="d-flex justify-content-between mt-2 pt-2"
                              style={{ borderTop: "1.5px solid #e9ecef" }}
                            >
                              <span style={{ fontWeight: 700, fontSize: 14 }}>Due today</span>
                              <span style={{ fontWeight: 700, fontSize: 16, color: "#34c38f" }}>
                                ${(monthlyTotal + setupFee * selectedNumbers.length).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Payment form */}
                      <form onSubmit={handleCheckout}>
                        <div className="mb-2">
                          <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>Cardholder Name</label>
                          <div className="input-group">
                            <span
                              className="input-group-text"
                              style={{ background: "#f8f9ff", border: "1.5px solid #e9ecef", borderRight: 0 }}
                            >
                              <i className="bx bx-user text-primary" />
                            </span>
                            <input
                              type="text"
                              className="form-control"
                              style={{ border: "1.5px solid #e9ecef", borderLeft: 0 }}
                              placeholder="Full name on card"
                              value={cardholderName}
                              onChange={(e) => setCardholderName(e.target.value)}
                              required
                            />
                          </div>
                        </div>

                        <div className="mb-2">
                          <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>Card Details</label>
                          <div
                            style={{
                              border: "1.5px solid #e9ecef",
                              borderRadius: 10,
                              padding: "12px 14px",
                              background: "#fff",
                            }}
                          >
                            <CardElement
                              options={CARD_STYLE}
                              onChange={(e) => setCardError(e.error?.message ?? null)}
                            />
                          </div>
                          {cardError && (
                            <small className="text-danger d-block mt-1">
                              <i className="bx bx-error-circle me-1" />{cardError}
                            </small>
                          )}
                        </div>

                        <div
                          className="form-check p-3 mb-3"
                          style={{ background: "#f8f9ff", borderRadius: 10, border: "1.5px solid #e0e4ff" }}
                        >
                          <input
                            className="form-check-input mt-0"
                            type="checkbox"
                            checked={isAgreed}
                            onChange={(e) => setIsAgreed(e.target.checked)}
                            id="agreeTerms"
                            style={{ width: 18, height: 18 }}
                          />
                          <label className="form-check-label ms-2" htmlFor="agreeTerms" style={{ fontSize: 13 }}>
                            I agree to the{" "}
                            <a
                              href="#"
                              data-bs-toggle="modal"
                              data-bs-target="#termsModal"
                              onClick={(e) => e.preventDefault()}
                            >
                              terms and conditions
                            </a>{" "}
                            and authorize recurring monthly charges.
                          </label>
                        </div>

                        <div className="d-flex justify-content-between">
                          <button
                            type="button"
                            className="btn btn-light px-4"
                            style={{ borderRadius: 10, fontWeight: 600 }}
                            onClick={() => setActiveTab(2)}
                            disabled={checkoutLoader}
                          >
                            <i className="bx bx-chevron-left me-1" />Back
                          </button>
                          <button
                            type="submit"
                            className="btn btn-success px-5"
                            style={{ borderRadius: 10, fontWeight: 700, fontSize: 15 }}
                            disabled={checkoutLoader || !isAgreed || selectedNumbers.length === 0 || !stripe}
                          >
                            {checkoutLoader ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Processing…
                              </>
                            ) : (
                              <>
                                <i className="bx bx-lock-alt me-2" />
                                Subscribe &amp; Pay
                              </>
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Terms Modal */}
      <div className="modal fade" id="termsModal">
        <div className="modal-dialog">
          <div className="modal-content" style={{ borderRadius: 12 }}>
            <div className="modal-header" style={{ borderBottom: "1.5px solid #e9ecef" }}>
              <h5 className="modal-title fw-bold">Terms and Conditions</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" />
            </div>
            <div className="modal-body" style={{ fontSize: 14, color: "#495057" }}>
              By subscribing, you authorize recurring monthly charges for each fax number. You may cancel
              at any time from the Fax Number Subscriptions page. Cancellations take effect at the end of
              the current billing period.
            </div>
            <div className="modal-footer" style={{ borderTop: "1.5px solid #e9ecef" }}>
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.auth.user,
});

export default connect(mapStateToProps, { actionCreateStripeCustomer })(FaxPhoneNumbers);
